import React, { useState, useEffect } from 'react';
import {
  Space,
  Text,
  Icon,
  Conditional,
  Button,
  Toggle,
} from '@cashfree-intl/coherent';

import { Image } from 'components/Image';
import { isNumberUptoTwoDecimalPlaces } from 'utils/common';

import {
  StyledDivider,
  StyledPaypalTransactionCharges,
  StyledSavings,
  StyledInputField,

  MarkupBanner,
  StyledForexMarkupStatus,
} from '../styled';

import ForexMarkupDetails from './ForexMarkupDetails';
import { DEFAULT_FOREX_INPUT_VALUE, DEFAULT_MARKUP, FOREX_MARKUP_KNOW_MORE_LINK, FOREX_MARKUP_VALIDATION } from './constants';
import { calculateForexMarkup } from './helpers';
import { findEffectiveSavings } from '../utils';
import pitchImage from 'images/industry-first-feature.svg';

const ForexMarkup = ({
  markup,
  onMarkupSubmit,
  onMarkupUpdate,
  isForexMarkupEnabled,
  setForexMarkup,
}: {
  markup: number;
  isForexMarkupEnabled: boolean;
  onMarkupSubmit: () => void;
  onMarkupUpdate: () => void;
  setForexMarkup: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [forexCalculation, setForexCalculation] = useState<any>(
    calculateForexMarkup(markup || DEFAULT_MARKUP),
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string>('');
  const [forexMarkupForDisplay, setForexMarkupForDisplay] = useState<number>(
    markup || DEFAULT_MARKUP,
  );

  useEffect(() => {
    if (!inputError) {
      const result = calculateForexMarkup(markup || DEFAULT_MARKUP);
      setForexCalculation(result);
      setForexMarkupForDisplay(markup || DEFAULT_MARKUP);
    }
  }, [markup]);

  const openKnowMoreLink = () => window.open(FOREX_MARKUP_KNOW_MORE_LINK, '_blank');

  const onForexMarkupSubmit = (
    buttonClicked: boolean,
  ): (() => void) => (): void => {
    if (!inputError && !!markup) {
      setIsEditMode(false);
      if (isForexMarkupEnabled && buttonClicked) onMarkupUpdate();
      else onMarkupSubmit();
    } else {
      setInputError(`Markup value should be between ${FOREX_MARKUP_VALIDATION.MIN} and ${FOREX_MARKUP_VALIDATION.MAX}%`);
      setIsEditMode(true);
    }
  };

  const onForexMarukupChange = e => {
    const { value } = e.target;
    if (value && !isNumberUptoTwoDecimalPlaces(value)) return;

    setForexMarkup(value);

    const { MIN, MAX } = FOREX_MARKUP_VALIDATION;

    let error = '';
    if (!value || value < FOREX_MARKUP_VALIDATION.MIN) error = `Markup value should be between ${MIN} and ${MAX}%`;
    else if (value < MIN)
      error = `Markup should be more than ${MIN}`;
    else if (value > MAX)
      error = `Markup should be less than ${MAX}`;

    setInputError(error);
  };

  return (
    <div>
      <StyledDivider />
      <MarkupBanner className="px-3 py-1 mb-4" alignItems="center" gap={3}>
        <Image src={pitchImage} alt="industry-first-feature" />
        <Space alignItems="flex-end" justifyContent="space-between" className="w-100">
          <div>
            <Text color="white" variant="h28" strong>
              Now save upto 75%
            </Text>
            <Text color="white" variant="h16">
              On costs incurred on PayPal transactions
            </Text>
            <Text color="white" variant="h16" className="mt-3">
              Easily markup charges from your international customers
            </Text>
          </div>
          <Button onClick={openKnowMoreLink}>
            <Text color="focused">Know More</Text>
          </Button>
        </Space>
      </MarkupBanner>
      <Space justifyContent="space-between">
        <div>
          <Space alignItems="center" className="mb-3" gap={5} wrap="wrap">
            <Text color="bodyLight">
              Charge Markup Toggle
            </Text>
            <StyledForexMarkupStatus alignItems="center" gap={3}>
              <Text color={isForexMarkupEnabled ? 'success' : 'bodyLight'}>
                {isForexMarkupEnabled ? 'Active' : 'Inactive'}
              </Text>
              <Toggle
                active={isForexMarkupEnabled}
                onToggle={onForexMarkupSubmit(false)}
              />
            </StyledForexMarkupStatus>
          </Space>
          <StyledPaypalTransactionCharges justifyContent="space-between" gap={5} alignItems="baseline" wrap="wrap">
            <Text color="bodyLight">
              Markup Percentage Value
            </Text>
            <Conditional if={!isEditMode}>
              <Space gap={2} alignItems="center">
                <Text>{markup || DEFAULT_FOREX_INPUT_VALUE}%</Text>
                <Icon
                  name="edit"
                  className="pointer"
                  onClick={() => setIsEditMode(true)}
                />
              </Space>
            </Conditional>
            <Conditional if={isEditMode}>
              <div>
                <Space alignItems="flex-start" gap={1}>
                  <StyledInputField
                    label=""
                    value={markup || ''}
                    name="forexMarkup"
                    placeholder="0"
                    type="number"
                    step="0.01"
                    onChange={onForexMarukupChange}
                    max="5"
                    min="1"
                  />
                  <Button
                    small
                    disabled={!!inputError}
                    secondary
                    onClick={onForexMarkupSubmit(true)}
                    data-event-name="Secondary_Button_Forex_Markup_Submit"
                    data-ignore-inner-text="true"
                  >
                    Submit
                  </Button>
                </Space>
                <Conditional if={!!inputError}>
                  <Text variant="b12" color="danger" className="mt-1">{inputError}</Text>
                </Conditional>
              </div>
            </Conditional>
          </StyledPaypalTransactionCharges>
        </div>
        <StyledSavings>
          <Text color="bodyLight">Effective savings for transactions on USD Paypal<br /> transactions</Text>
          <Text variant="h28">
            <Conditional if={forexCalculation?.effectiveCost}>
              <span>{findEffectiveSavings(forexCalculation?.effectiveCost)}%</span>
            </Conditional>
          </Text>
        </StyledSavings>
      </Space>
      <ForexMarkupDetails
        forexMarkup={forexMarkupForDisplay}
        forexCalculation={forexCalculation}
        isForexMarkupEnabled={isForexMarkupEnabled}
      />
      <Text color="bodyLight" className="mt-4">
        The markup charged to the customer shall cover full/partial costs of your PayPal transactions. <br />
        Please note that this is an explicit opt-in feature & Cashfree payments will not be liable for any concerns raised by your customers. <br />
        Forex markup is only applicable for orders created in INR currency.
      </Text>
    </div>
  );
};

export default ForexMarkup;
